export const MAX_BIN = 10;
export const NET_VOL_STEP = 1000;
export const NET_VOL_ZERO_DEAD = 1000;
export const NET_DELTA_STEP = 250;
export const NET_DELTA_ZERO_DEAD = 250;
export const NET_GAMMA_STEP = 10;
export const NET_GAMMA_ZERO_DEAD = 10;
export const OPTION_CHAIN_HEAT_PALETTE = {
  neutral: '#000000',
  negative: [
    '#000000',
    '#1f0a10',
    '#330e18',
    '#4a0c1d',
    '#610820',
    '#780020',
    '#8f001f',
    '#a6001a',
    '#bb0012',
    '#d00000',
  ],
  positive: [
    '#000000',
    '#0e190d',
    '#142b15',
    '#183e1a',
    '#1b5120',
    '#1c6625',
    '#1c7b2b',
    '#199130',
    '#12a835',
    '#00bf3a',
  ],
};

export const OPTION_CHAIN_NEUTRAL_HEX = OPTION_CHAIN_HEAT_PALETTE.neutral;
export const OPTION_CHAIN_SHADE_RAMP = {
  neg: OPTION_CHAIN_HEAT_PALETTE.negative,
  pos: OPTION_CHAIN_HEAT_PALETTE.positive,
};

const optionChainNegativeCssVariables = OPTION_CHAIN_HEAT_PALETTE.negative.reduce((variables, color, index) => {
  variables[`--oc-heat-negative-${index + 1}`] = color;
  return variables;
}, {});
const optionChainPositiveCssVariables = OPTION_CHAIN_HEAT_PALETTE.positive.reduce((variables, color, index) => {
  variables[`--oc-heat-positive-${index + 1}`] = color;
  return variables;
}, {});

export const OPTION_CHAIN_HEAT_CSS_VARIABLES = {
  '--oc-heat-neutral': OPTION_CHAIN_HEAT_PALETTE.neutral,
  ...optionChainNegativeCssVariables,
  ...optionChainPositiveCssVariables,
};

const LAYER_BUCKETS = {
  volume: { step: NET_VOL_STEP, zeroDead: NET_VOL_ZERO_DEAD, maxBin: MAX_BIN },
  delta: { step: NET_DELTA_STEP, zeroDead: NET_DELTA_ZERO_DEAD, maxBin: MAX_BIN },
  d: { step: NET_DELTA_STEP, zeroDead: NET_DELTA_ZERO_DEAD, maxBin: MAX_BIN },
  gamma: { step: NET_GAMMA_STEP, zeroDead: NET_GAMMA_ZERO_DEAD, maxBin: MAX_BIN },
  g: { step: NET_GAMMA_STEP, zeroDead: NET_GAMMA_ZERO_DEAD, maxBin: MAX_BIN },
};

export function signedBucket(value, layer = 'gamma') {
  const config = LAYER_BUCKETS[layer] || LAYER_BUCKETS.gamma;
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const abs = Math.abs(n);
  if (abs < config.zeroDead) return 0;
  return Math.min(config.maxBin, Math.max(1, Math.floor(abs / config.step)));
}

export function bucketNormalizedValue(value, layer = 'gamma') {
  const config = LAYER_BUCKETS[layer] || LAYER_BUCKETS.gamma;
  const bin = signedBucket(value, layer);
  if (!bin) return 0;
  return (Number(value) < 0 ? -1 : 1) * (bin / config.maxBin);
}

export function normalizedShadeBin(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.min(MAX_BIN, Math.round(Math.abs(n) * MAX_BIN));
}

export function chainShadeHexFromNormalizedValue(value) {
  const n = Number(value);
  const bin = normalizedShadeBin(n);
  if (!bin) return OPTION_CHAIN_NEUTRAL_HEX;
  const ramp = n < 0 ? OPTION_CHAIN_SHADE_RAMP.neg : OPTION_CHAIN_SHADE_RAMP.pos;
  return ramp[bin - 1];
}

// Map NetVol number -> CSS class in ±1000 buckets, capped at 10
export function netVolClass(v, opts = {}) {
  const { step = NET_VOL_STEP, maxBin = MAX_BIN, zeroDead = NET_VOL_ZERO_DEAD, invert = false } = opts;

  if (v == null || Number.isNaN(v)) return 'oc-nv oc-nv-zero';

  const abs = Math.abs(Number(v));
  if (abs < zeroDead) return 'oc-nv oc-nv-zero';

  let bin = Math.floor(abs / step);
  if (bin < 1) bin = 1;
  if (bin > maxBin) bin = maxBin;

  const colorValue = invert ? -Number(v) : Number(v);
  return `oc-nv oc-nv-${colorValue < 0 ? 'neg' : 'pos'}-${bin}`;
}

// Map Net Delta -> CSS class (±100 steps, 10 bins)
export function deltaClass(v, opts = {}) {
  const { step = NET_DELTA_STEP, maxBin = MAX_BIN, zeroDead = NET_DELTA_ZERO_DEAD } = opts; // 100-pt bins, dead zone ~½ step
  if (v == null || Number.isNaN(v)) return 'oc-dn oc-dn-zero'
  const abs = Math.abs(Number(v));
  if (abs < zeroDead) return 'oc-dn oc-dn-zero';
  let bin = Math.floor(abs / step);
  if (bin < 1) bin = 1;
  if (bin > maxBin) bin = maxBin;
  return `oc-dn oc-dn-${v < 0 ? 'neg' : 'pos'}-${bin}`;
}

// Map Net Gamma -> CSS class (±10 steps, 10 bins)
export function gammaClass(v, opts = {}) {
  const { step = NET_GAMMA_STEP, maxBin = MAX_BIN, zeroDead = NET_GAMMA_ZERO_DEAD } = opts; // 10-pt bins, dead zone ~½ step
  if (v == null || Number.isNaN(v)) return 'oc-gn oc-gn-zero';
  const abs = Math.abs(Number(v));
  if (abs < zeroDead) return 'oc-gn oc-gn-zero';
  let bin = Math.floor(abs / step);
  if (bin < 1) bin = 1;
  if (bin > maxBin) bin = maxBin;
  return `oc-gn oc-gn-${v < 0 ? 'neg' : 'pos'}-${bin}`;
}

// Map Trade Count -> CSS class in 10 bins, default 25 trades per bin
export function tradeCountClass(v, opts = {}) {
  const { step = 5000, maxBin = 10, zeroDead = 5000 } = opts; // tweak step later if you want
  const n = Number(v);
  if (!Number.isFinite(n) || n <= zeroDead) return 'oc-tr oc-tr-zero';
  let bin = Math.ceil(n / step);     // 1..step -> bin1, step+1..2*step -> bin2, ...
  if (bin > maxBin) bin = maxBin;    // cap overflow at 10
  return `oc-tr oc-tr-${bin}`;
}

// Map Volume -> CSS class in 10k buckets, capped at 10
export function volumeClass(v, opts = {}) {
  const { step = 10000, maxBin = 10, zeroDead = 50000 } = opts; // 10k per bucket
  const n = Number(v);
  if (!Number.isFinite(n) || n <= zeroDead) return 'oc-vol oc-vol-zero';

  let bin = Math.ceil(n / step); // 1..step -> s1, step+1..2*step -> s2, etc.
  if (bin > maxBin) bin = maxBin;

  return `oc-vol oc-vol-s${bin}`;
}
